import { useEffect, useMemo, useState } from 'react';
import { api, type LastStageConfig, type RangeSession, type TargetRow } from '../lib/api';
import { errorText } from '../lib/errorText';
import { useUncalibrated } from '../lib/calibration';
import { UncalibratedBadge } from '../components/UncalibratedBadge';

export interface CustomConfig {
  name: string;
  targetTypeId: string;
  targetName: string;
  shotCount: number;
  /** Magazine breakdown (sum === shotCount) or null = single magazine. */
  magazines: Array<{ shots: number }> | null;
  saveAsDraft: boolean;
}

interface Props {
  session: RangeSession;
  /** When set ("Vorheriges wiederholen"), pre-fills target + shot count. */
  prefill: LastStageConfig | null;
  onConfirm: (cfg: CustomConfig) => void;
  onBack: () => void;
}

/** Auto-name a custom drill from its target + shot count. */
function autoName(targetName: string, shotCount: number): string {
  return `Custom · ${targetName} · ${shotCount} Schuss`;
}

/**
 * Step 0 of the custom-drill flow: the referee picks a target and shot
 * count (the bits that have no equivalent in the catalog-pick flow), then
 * continues into the shared mode / distance / PAR pickers. Distance and PAR
 * are chosen in those later steps; here we only gather target, count, name
 * and the "save as draft" choice.
 */
export function CustomDrillScreen({ session, prefill, onConfirm, onBack }: Props) {
  const [targets, setTargets] = useState<TargetRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [targetTypeId, setTargetTypeId] = useState<string>(prefill?.targetTypeId ?? '');
  const [shotCount, setShotCount] = useState<number>(prefill?.shotCount ?? 10);
  // Per-magazine shot counts; null = single magazine (flat shot count).
  // Pre-fills from the previous stage's breakdown when repeating.
  const [mags, setMags] = useState<number[] | null>(
    prefill?.magazines && prefill.magazines.length > 0
      ? prefill.magazines.map((m) => m.shots)
      : null,
  );
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  // Manual name override: once the referee types, stop auto-deriving.
  const [nameOverride, setNameOverride] = useState<string | null>(null);
  // Bumped by "Erneut versuchen" to reload the targets.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setError(null);
    void api
      .targets()
      .then(setTargets)
      .catch((err) => setError(errorText(err)));
  }, [attempt]);

  const isUncalibrated = useUncalibrated(session.locationId);
  const selected = useMemo(
    () => targets?.find((t) => t.target.id === targetTypeId) ?? null,
    [targets, targetTypeId],
  );
  const targetName = selected?.target.name ?? prefill?.targetName ?? '';
  // When split into magazines the total is their sum (source of truth);
  // otherwise the flat shotCount input.
  const effectiveShotCount = mags ? mags.reduce((acc, n) => acc + n, 0) : shotCount;
  const magsValid = mags == null || (mags.length > 0 && mags.every((n) => n > 0));
  const derivedName = autoName(targetName, effectiveShotCount);
  const name = nameOverride ?? derivedName;

  const canContinue =
    targetTypeId !== '' && effectiveShotCount > 0 && magsValid && name.trim() !== '';

  function setMag(idx: number, value: number) {
    setMags((prev) =>
      prev ? prev.map((n, i) => (i === idx ? Math.max(1, Math.floor(value || 0)) : n)) : prev,
    );
  }

  function submit() {
    if (!canContinue || !selected) return;
    onConfirm({
      name: name.trim(),
      targetTypeId,
      targetName: selected.target.name,
      shotCount: effectiveShotCount,
      magazines: mags ? mags.map((shots) => ({ shots })) : null,
      saveAsDraft,
    });
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <button onClick={onBack} className="min-h-12 pr-3 text-sm text-zinc-500 underline">
          ← zurück
        </button>
        <h2 className="text-lg font-bold">Eigene Übung</h2>
      </header>

      {prefill && (
        <div className="rounded border border-emerald-700 bg-emerald-900/20 px-4 py-3 text-sm text-emerald-200">
          Vorlage aus „{prefill.name}“ übernommen — Scheibe & Schusszahl vorbelegt, Distanz und PAR
          kannst du in den nächsten Schritten anpassen.
        </div>
      )}

      {error && (
        <div role="alert" className="space-y-3 rounded bg-red-900/30 p-3 text-sm text-red-300">
          <p>Fehler: {error}</p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100 hover:bg-red-900/40"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <label className="block">
        <span className="text-sm text-zinc-400">Scheibe</span>
        {targets == null ? (
          <p className="mt-1 text-sm text-zinc-500">Lade Scheiben…</p>
        ) : targets.length === 0 ? (
          <p className="mt-1 text-sm text-amber-300">
            Keine Scheiben angelegt — im Admin-Bereich „Scheiben“ anlegen.
          </p>
        ) : (
          <select
            value={targetTypeId}
            onChange={(e) => setTargetTypeId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-3 text-lg font-medium text-zinc-100 focus:border-emerald-500 focus:outline-none"
          >
            <option value="">– Scheibe wählen –</option>
            {targets.map((t) => (
              <option key={t.target.id} value={t.target.id}>
                {t.target.name} ({t.schemaName})
                {isUncalibrated(t.target.id) ? ' — ⚠ nicht kalibriert' : ''}
              </option>
            ))}
          </select>
        )}
        {isUncalibrated(targetTypeId) && (
          <div className="mt-2">
            <UncalibratedBadge />
          </div>
        )}
      </label>

      {mags == null ? (
        <div className="space-y-2">
          <label className="block">
            <span className="text-sm text-zinc-400">Schüsse</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              value={shotCount}
              onChange={(e) => setShotCount(Math.max(1, Math.floor(Number(e.target.value) || 0)))}
              className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-3 text-lg font-medium tabular-nums text-zinc-100 focus:border-emerald-500 focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={() => setMags([shotCount])}
            className="min-h-12 text-xs text-emerald-400 underline"
          >
            In Magazine aufteilen
          </button>
        </div>
      ) : (
        <div className="space-y-2 rounded-lg border border-zinc-700 bg-zinc-900 p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-400">Magazine</span>
            <span className="text-sm font-semibold tabular-nums text-zinc-200">
              Σ {effectiveShotCount} Schuss
            </span>
          </div>
          {mags.map((shots, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="w-20 text-sm text-zinc-400">Magazin {idx + 1}</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={shots}
                onChange={(e) => setMag(idx, Number(e.target.value))}
                className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-base font-medium tabular-nums text-zinc-100 focus:border-emerald-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setMags((prev) => (prev ? prev.filter((_, i) => i !== idx) : prev))}
                disabled={mags.length <= 1}
                className="min-h-12 min-w-12 rounded px-2 py-1 text-sm text-red-400 disabled:opacity-30"
                aria-label={`Magazin ${idx + 1} entfernen`}
              >
                ✕
              </button>
            </div>
          ))}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={() => setMags((prev) => [...(prev ?? []), prev?.[prev.length - 1] ?? 1])}
              className="min-h-12 text-xs text-emerald-400 underline"
            >
              + Magazin hinzufügen
            </button>
            <button
              type="button"
              onClick={() => {
                setShotCount(effectiveShotCount);
                setMags(null);
              }}
              className="min-h-12 text-xs text-zinc-500 underline"
            >
              Aufteilung entfernen
            </button>
          </div>
        </div>
      )}

      <label className="block">
        <span className="text-sm text-zinc-400">Name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setNameOverride(e.target.value)}
          placeholder={derivedName}
          className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-3 text-base text-zinc-100 focus:border-emerald-500 focus:outline-none"
        />
        {nameOverride !== null && (
          <button
            type="button"
            onClick={() => setNameOverride(null)}
            className="mt-1 min-h-12 text-xs text-zinc-500 underline"
          >
            Automatischen Namen verwenden
          </button>
        )}
      </label>

      <label className="flex items-center gap-3 rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-3">
        <input
          type="checkbox"
          checked={saveAsDraft}
          onChange={(e) => setSaveAsDraft(e.target.checked)}
          className="h-5 w-5"
        />
        <span className="text-sm text-zinc-200">
          Als Entwurf speichern
          <span className="block text-xs text-zinc-500">
            Zur späteren Prüfung & Freigabe im Admin-Bereich.
          </span>
        </span>
      </label>

      <div className="pt-2">
        <button
          type="button"
          onClick={submit}
          disabled={!canContinue}
          className="min-h-12 w-full rounded-lg bg-emerald-700 px-4 py-4 text-lg font-bold text-white transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Weiter
        </button>
      </div>
    </section>
  );
}
